import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Button, Card, CardContent, Chip, Divider, IconButton} from 'views/components';
import {Grid2 as Grid, Stack, Tooltip, Typography} from 'views/components';
import {Autorenew as RegenerateIcon, Edit as EditIcon} from 'views/icons';
import {Translate, translate} from 'views/module';

import JobStatusMenu from './JobStatusMenu';

import {displayCode} from 'utils/backend/loom/job/job_adapter';

const ClientJobCard = (props) => {
  const {job, onEdit, onRegenerate, onStatusChange} = props;

  return (
    <Card
      variant='outlined'
      sx={{
        borderRadius: '12px',
        borderColor: '#E8E4DE',
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Stack direction='row' spacing={1} sx={{alignItems: 'flex-start', justifyContent: 'space-between'}}>
            <Stack spacing={0.5} sx={{minWidth: 0}}>
              <Typography sx={{fontWeight: 600, overflowWrap: 'anywhere'}}>
                {job.summary}
              </Typography>
              <Typography sx={{fontSize: '13px', color: 'text.secondary'}}>
                {`${translate('job.ClientJobs.Created')} ${new Date(job.createdAt).toLocaleDateString('en-CA')}`}
              </Typography>
            </Stack>
            <Stack direction='row' spacing={1} sx={{alignItems: 'center', flexShrink: 0}}>
              <JobStatusMenu
                status={job.status}
                editable={job.editable}
                onChange={(status) => onStatusChange(job, status)}
              />
              {
                job.editable &&
                <>
                  <Tooltip title={translate('job.ClientJobs.Edit')}>
                    <IconButton
                      size='small'
                      aria-label={translate('job.ClientJobs.Edit')}
                      onClick={() => onEdit(job)}
                    >
                      <EditIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<RegenerateIcon />}
                    onClick={() => onRegenerate(job)}
                  >
                    <Translate expression='job.ClientJobs.Regenerate' />
                  </Button>
                </>
              }
            </Stack>
          </Stack>
          {
            !job.editable &&
            <Alert severity='info'>
              <Translate expression='job.ClientJobs.SyncedFromCareerFe' />
            </Alert>
          }
          <Grid container spacing={2}>
            <Grid size={{xs: 6, md: 2}}>
              <Stack spacing={0.5}>
                <Typography sx={{fontSize: '12px', color: 'text.secondary'}}>
                  <Translate expression='job.ClientJobs.Rate' />
                </Typography>
                <Typography sx={{fontSize: '14px'}}>
                  {`$${job.hourlyRate.toFixed(2)}${translate('job.ClientJobs.PerHour')}`}
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{xs: 6, md: 2}}>
              <Stack spacing={0.5}>
                <Typography sx={{fontSize: '12px', color: 'text.secondary'}}>
                  <Translate expression='job.ClientJobs.Start' />
                </Typography>
                <Typography sx={{fontSize: '14px'}}>
                  {translate(`job.ClientJobs.StartWindows.${job.startWindow}`)}
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{xs: 12, md: 8}}>
              <Stack spacing={0.5}>
                <Typography sx={{fontSize: '12px', color: 'text.secondary'}}>
                  <Translate expression='job.ClientJobs.Schedule' />
                </Typography>
                <Stack direction='row' sx={{flexWrap: 'wrap', gap: 0.5}}>
                  {
                    job.scheduleSlots.length === 0 &&
                    <Typography sx={{fontSize: '14px', color: 'text.secondary'}}>
                      <Translate expression='job.ClientJobs.NoSchedule' />
                    </Typography>
                  }
                  {
                    job.scheduleSlots.map((slot) => (
                      <Chip
                        key={`${slot.dayOfWeek}-${slot.startTime}-${slot.endTime}`}
                        size='small'
                        variant='outlined'
                        label={`${translate(`job.ClientJobs.Days.${slot.dayOfWeek}`)} ${slot.startTime} to ${slot.endTime}`}
                      />
                    ))
                  }
                </Stack>
              </Stack>
            </Grid>
            {
              job.careNeeds.length > 0 &&
              <Grid size={{xs: 12, md: 6}}>
                <Stack spacing={0.5}>
                  <Typography sx={{fontSize: '12px', color: 'text.secondary'}}>
                    <Translate expression='job.ClientJobs.CareNeeds' />
                  </Typography>
                  <Stack direction='row' sx={{flexWrap: 'wrap', gap: 0.5}}>
                    {
                      job.careNeeds.map((need) => (
                        <Chip key={need} size='small' label={displayCode(need)} />
                      ))
                    }
                  </Stack>
                </Stack>
              </Grid>
            }
            {
              job.languages.length > 0 &&
              <Grid size={{xs: 12, md: 6}}>
                <Stack spacing={0.5}>
                  <Typography sx={{fontSize: '12px', color: 'text.secondary'}}>
                    <Translate expression='job.ClientJobs.Languages' />
                  </Typography>
                  <Stack direction='row' sx={{flexWrap: 'wrap', gap: 0.5}}>
                    {
                      job.languages.map((language) => (
                        <Chip
                          key={language.name}
                          size='small'
                          label={language.level === 'NICE_TO_HAVE' ?
                            `${displayCode(language.name)} (${translate('job.ClientJobs.NiceToHave')})` :
                            displayCode(language.name)}
                        />
                      ))
                    }
                  </Stack>
                </Stack>
              </Grid>
            }
          </Grid>
          <Divider />
          <Typography sx={{fontSize: '14px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>
            {job.description}
          </Typography>
          {
            job.additionalConsiderations &&
            <Box>
              <Typography sx={{fontSize: '12px', color: 'text.secondary'}}>
                <Translate expression='job.ClientJobs.Considerations' />
              </Typography>
              <Typography sx={{fontSize: '14px', whiteSpace: 'pre-wrap'}}>
                {job.additionalConsiderations}
              </Typography>
            </Box>
          }
        </Stack>
      </CardContent>
    </Card>
  );
};

ClientJobCard.propTypes = {
  job: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRegenerate: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
};

export default React.memo(ClientJobCard);
